import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import './FeaturedTab.css';
import useCatalogManagementStore from '@/stores/AdminStore/CatalogManagementStore';
import { ActiveCourse } from '@/services/PagesServices/CatalogManagementService';

interface RecommendationRuleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
  editRuleId?: number | null;
  editRuleData?: {
    rule_name: string;
    rule_condition: string;
    courses: Array<{ id: string; title: string }>;
  } | null;
}

const RecommendationRuleModal: React.FC<RecommendationRuleModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  onError,
  editRuleId = null,
  editRuleData = null,
}) => {
  const { activeCourses, fetchActiveCourses, createRecommendationRule, editRecommendationRule, fetchRecommendationRules } = useCatalogManagementStore();

  const isEditMode = editRuleId !== null;

  const [ruleFormData, setRuleFormData] = useState({
    rule_name: '',
    rule_condition: '',
    courses: [] as Array<{ id: string; title: string }>,
    courseInput: '',
  });

  const [showCourseDropdown, setShowCourseDropdown] = useState(false);
  const [filteredCourses, setFilteredCourses] = useState<ActiveCourse[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Initialize form data when modal opens or edit data changes
  useEffect(() => {
    if (isOpen) {
      if (editRuleData) {
        setRuleFormData({
          rule_name: editRuleData.rule_name,
          rule_condition: editRuleData.rule_condition,
          courses: editRuleData.courses,
          courseInput: '',
        });
      } else {
        setRuleFormData({ rule_name: '', rule_condition: '', courses: [], courseInput: '' });
      }
      // Fetch active courses when modal opens
      fetchActiveCourses().catch((error) => {
        console.error('Failed to fetch active courses:', error);
      });
    }
  }, [isOpen, editRuleData, fetchActiveCourses]);

  // Filter courses based on search input
  useEffect(() => {
    if (activeCourses && ruleFormData.courseInput) {
      const filtered = activeCourses.filter(course =>
        course.title.toLowerCase().includes(ruleFormData.courseInput.toLowerCase())
      );
      setFilteredCourses(filtered);
    } else {
      setFilteredCourses(activeCourses || []);
    }
  }, [activeCourses, ruleFormData.courseInput]);

  const handleCourseInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setRuleFormData({ ...ruleFormData, courseInput: value });
    setShowCourseDropdown(true);
  };

  const selectCourse = (course: ActiveCourse) => {
    // Check if course is already added
    if (!ruleFormData.courses.find(c => c.id === course.id)) {
      setRuleFormData({
        ...ruleFormData,
        courses: [...ruleFormData.courses, { id: course.id, title: course.title }],
        courseInput: '',
      });
    }
    setShowCourseDropdown(false);
  };

  const removeCourseFromRule = (courseId: string) => {
    setRuleFormData({
      ...ruleFormData,
      courses: ruleFormData.courses.filter(c => c.id !== courseId),
    });
  };

  const handleCourseInputKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setShowCourseDropdown(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate that at least one course is selected
    if (ruleFormData.courses.length === 0) {
      onError('Please select at least one course for the recommendation rule');
      return;
    }

    setIsLoading(true);
    try {
      // Extract course IDs from the courses array
      const courseIds = ruleFormData.courses.map(course => parseInt(course.id));

      if (isEditMode && editRuleId) {
        // Update existing rule
        await editRecommendationRule(
          editRuleId,
          ruleFormData.rule_name,
          ruleFormData.rule_condition,
          courseIds
        );
        // Refetch to get updated data with course names
        await fetchRecommendationRules();
        onSuccess('Recommendation rule updated successfully!');
      } else {
        // Create new rule
        await createRecommendationRule(
          ruleFormData.rule_name,
          ruleFormData.rule_condition,
          courseIds
        );
        // Refetch to get updated data with course names
        await fetchRecommendationRules();
        onSuccess('Recommendation rule created successfully!');
      }
      onClose();
    } catch (error: any) {
      console.error(`Failed to ${isEditMode ? 'update' : 'create'} recommendation rule:`, error);
      onError(error.message || `Failed to ${isEditMode ? 'update' : 'create'} recommendation rule. Please try again.`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setRuleFormData({ rule_name: '', rule_condition: '', courses: [], courseInput: '' });
    setShowCourseDropdown(false);
    onClose();
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="admin-catalog-modal-overlay" onClick={handleClose}>
      <div className="admin-catalog-modal-content admin-catalog-rule-modal" onClick={(e) => e.stopPropagation()}>
        <div className="admin-catalog-modal-header">
          <div className="admin-catalog-modal-header-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path
                d="M22 12h-4l-3 9L9 3l-3 9H2"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <h2 className="admin-catalog-modal-title">{isEditMode ? 'Edit Recommendation Rule' : 'Create Recommendation Rule'}</h2>
          <button className="admin-catalog-modal-close" onClick={handleClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="admin-catalog-form-group">
            <label htmlFor="ruleName" className="admin-catalog-form-label">
              Rule Name <span className="admin-catalog-required">*</span>
            </label>
            <input
              type="text"
              id="ruleName"
              className="admin-catalog-form-input"
              placeholder="e.g., New Joiners Onboarding"
              value={ruleFormData.rule_name}
              onChange={(e) => setRuleFormData({ ...ruleFormData, rule_name: e.target.value })}
              required
            />
          </div>

          <div className="admin-catalog-form-group">
            <label htmlFor="ruleCondition" className="admin-catalog-form-label">
              Condition <span className="admin-catalog-required">*</span>
            </label>
            <input
              type="text"
              id="ruleCondition"
              className="admin-catalog-form-input"
              placeholder="e.g., Show to employees with tenure < 30 days"
              value={ruleFormData.rule_condition}
              onChange={(e) => setRuleFormData({ ...ruleFormData, rule_condition: e.target.value })}
              required
            />
            <p className="admin-catalog-form-hint">Describe when this rule should trigger. This is for documentation purposes.</p>
          </div>

          <div className="admin-catalog-form-group">
            <label htmlFor="recommendedCourses" className="admin-catalog-form-label">
              Recommended Courses <span className="admin-catalog-required">*</span>
            </label>
            <div className="admin-catalog-course-input-wrapper" style={{ position: 'relative' }}>
              <input
                type="text"
                id="recommendedCourses"
                className="admin-catalog-form-input"
                placeholder="Search for courses..."
                value={ruleFormData.courseInput}
                onChange={handleCourseInputChange}
                onKeyDown={handleCourseInputKeyPress}
                onFocus={() => setShowCourseDropdown(true)}
              />
              <button
                type="button"
                className="admin-catalog-dropdown-toggle-btn"
                onClick={() => setShowCourseDropdown(!showCourseDropdown)}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>

              {/* Dropdown */}
              {showCourseDropdown && filteredCourses.length > 0 && (
                <div
                  className="admin-catalog-course-dropdown"
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    maxHeight: '200px',
                    overflowY: 'auto',
                    marginTop: '4px',
                    zIndex: 1000,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}
                >
                  {filteredCourses.map((course) => (
                    <div
                      key={course.id}
                      className="admin-catalog-course-dropdown-item"
                      onClick={() => selectCourse(course)}
                      style={{
                        padding: '10px 12px',
                        cursor: 'pointer'
                      }}
                    >
                      <div style={{ fontWeight: 500, fontSize: '14px' }}>
                        {course.title}
                      </div>
                      {course.description && (
                        <div style={{ fontSize: '12px', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {course.description}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {ruleFormData.courses.length > 0 && (
              <div className="admin-catalog-added-courses">
                {ruleFormData.courses.map((course, index) => (
                  <span key={index} className="admin-catalog-course-tag">
                    {course.title}
                    <button
                      type="button"
                      onClick={() => removeCourseFromRule(course.id)}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="admin-catalog-smart-recommendations-box">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path
                d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <div>
              <strong>Smart Recommendations</strong>
              <p>Recommendation rules automatically suggest relevant courses to learners based on conditions like role, department, tenure, or completion history.</p>
            </div>
          </div>

          <div className="admin-catalog-modal-actions">
            <button type="button" className="admin-catalog-btn-cancel" onClick={handleClose} disabled={isLoading}>
              Cancel
            </button>
            <button type="submit" className="admin-catalog-btn-save" disabled={isLoading}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path
                  d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M17 21v-8H7v8M7 3v5h8"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {isLoading ? 'Saving...' : (isEditMode ? 'Update Rule' : 'Create Rule')}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default RecommendationRuleModal;
